/* eslint-disable no-use-before-define */
import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Pagination from 'components/v2/Pagination';
import { DocumentMagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { humanize, titleize } from 'underscore.string';
import { sluggify } from 'utils/localization/utils';

import formatMoney from 'utils/formatters/Money';
import Checkbox from 'components/v2/forms/Checkbox';
import EditButton from 'components/v2/buttons/EditButton';
import Tooltip from 'components/v2/Tooltip';
import SessionStore from 'stores/SessionStore';
import Badge from 'components/v2/Badge';
import ArrowUpIcon from 'components/v2/icons/ArrowUpIcon';
import ArrowDownIcon from 'components/v2/icons/ArrowDownIcon';
import InfoIcon from 'components/v2/icons/InfoIcon';
import {
  getFileExtensionFromUrl,
  getFileIconClass,
} from 'helpers/helper_functions';

function renderInfoIcon() {
  return <InfoIcon />;
}

function formatTimeAgo(date) {
  return moment(date).fromNow(true);
}

function Link({ children, url }) {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      <div className="flex items-center space-x-1">
        <span className="flex-1">{children}</span>
      </div>
    </a>
  );
}

function StatusBadge(status, t) {
  let badgeColor;
  switch (status) {
    case 'confirmed':
      badgeColor = 'green';
      break;
    case 'awaiting':
      badgeColor = 'yellow';
      break;
    case 'expired':
      badgeColor = 'gray';
      break;
    case 'declined':
      badgeColor = 'red';
      break;
    default:
  }
  return (
    <Badge color={badgeColor} size="large">
      {humanize(t(sluggify(status)))}
    </Badge>
  );
}

function isEditButtonDisabled(customerType) {
  const isShipperLanePricingManager =
    SessionStore.isShipperLanePricingManager();
  const isCarrierLanePricingManager =
    SessionStore.isCarrierLanePricingManager();

  if (!(isShipperLanePricingManager && isCarrierLanePricingManager)) {
    return (
      (customerType !== 'shipper' && isShipperLanePricingManager) ||
      (customerType !== 'carrier' && isCarrierLanePricingManager)
    );
  }

  return !SessionStore.isAbleToEditLaneRate();
}

function renderEditButton(item, i, handleEditButtonClick) {
  return (
    <EditButton
      disabled={isEditButtonDisabled(item.customerType)}
      onClick={handleEditButtonClick(item, i)}
      size="small"
    />
  );
}

function renderAuditTrailButton(item, i, setRateAudit) {
  return (
    <button>
      <DocumentMagnifyingGlassIcon
        className="w-4 h-4 text-green"
        onClick={() => setRateAudit(item)}
      />
    </button>
  );
}

export default function List({
  items,
  perPage,
  page,
  pages,
  sortDirection,
  sortColumn,
  toggleSort,
  total,
  onPageClick,
  onEdit,
  onToggleCheck,
  onToggleCheckAll,
  checkAll,
  setRateAudit,
  isNewFscFlow,
  canUpdate,
}) {
  const { t } = useTranslation();

  function handleToggleCheckAll(checked) {
    onToggleCheckAll(checked);
  }

  function handlePageClick(selectedPage) {
    onPageClick(selectedPage);
  }

  function handleToggleCheck(item, index) {
    return function (checked) {
      onToggleCheck({ ...item, checked }, index);
    };
  }

  function handleEditButtonClick(item, index) {
    return function () {
      onEdit(item, index);
    };
  }

  function isRowChecked(item) {
    return item.checked;
  }

  function renderHeader(title, sortOptions) {
    return (
      <div className="flex whitespace-nowrap">
        {t(title)}
        {sortOptions.sortable
          ? renderSortIcon(sortOptions.sort, sortOptions.sortColumn)
          : null}
      </div>
    );
  }

  function renderSortIcon(sort, title) {
    return sortDirection === 'ASC' ? (
      <div onClick={(e) => toggleSort('DESC', title)}>
        <ArrowUpIcon className="w-3 ml-2" />
      </div>
    ) : (
      <div onClick={(e) => toggleSort('ASC', title)}>
        <ArrowDownIcon className="w-3 ml-2" />
      </div>
    );
  }

  function renderDocumentButton(item, index) {
    const requiredDoc = item?.document || item?.oldDocument;
    const url = requiredDoc ? requiredDoc?.signedUrl : '';
    const extension = getFileExtensionFromUrl(url) || '';

    if (requiredDoc && url) {
      return (
        <button>
          <a
            className={getFileIconClass(extension)}
            href={url}
            target="_blank"
            rel="noreferrer"
          >
            <img alt="" />
          </a>
        </button>
      );
    }
  }

  return (
    <div className="overflow-x-auto">
      <Pagination
        className="border-t-0"
        perPage={perPage}
        page={page}
        pages={pages}
        total={total}
        showCount
        paginationContainerClassName="mx-auto my-0 sticky right-2/4"
        onPageClick={handlePageClick}
      />
      <table className="default-table">
        <thead>
          <tr>
            <th>
              <Checkbox
                id="check-all"
                checked={checkAll}
                onChange={handleToggleCheckAll}
              />
            </th>
            <th>{t('USER', { count: 1 })}</th>
            <th>
              {renderHeader('CARRIER_SHIPPER', {
                sortColumn: 'name',
                sortable: true,
                sort: false,
              })}
            </th>
            <th>
              {renderHeader('ORIG_ZONE', {
                sortColumn: 'originZone',
                sortable: false,
                sort: false,
              })}
            </th>
            <th>
              {renderHeader('ORIG_ZIP', {
                sortColumn: 'originZip',
                sortable: true,
                sort: false,
              })}
            </th>
            <th>
              {renderHeader('DEST_ZONE', {
                sortColumn: 'destinationZone',
                sortable: false,
                sort: false,
              })}
            </th>
            <th>
              {renderHeader('DEST_ZIP', {
                sortColumn: 'destinationZip',
                sortable: true,
                sort: false,
              })}
            </th>
            <th>
              {renderHeader('RATE', {
                sortColumn: 'rate',
                sortable: true,
                sort: false,
              })}
            </th>

            {!isNewFscFlow && (
              <>
                <th>
                  {renderHeader('FSC', {
                    sortColumn: 'fsc',
                    sortable: true,
                    sort: false,
                  })}
                </th>
                <th>
                  {renderHeader('FSC_FLAT_RATE', {
                    sortColumn: 'fscFlatRate',
                    sortable: true,
                    sort: false,
                  })}
                </th>
              </>
            )}

            <th>
              <div className="flex whitespace-nowrap">
                {t('REVERSIBLE')}
                <Tooltip
                  id="rate-table-reversible"
                  body={renderInfoIcon}
                  position="top"
                >
                  {t('ORIGIN_DESTINATION_SAME_RATE_TIP')}
                </Tooltip>
              </div>
            </th>
            <th>
              {renderHeader('WEEKLY_COMMIT', {
                sortColumn: 'commitment',
                sortable: true,
                sort: false,
              })}
            </th>
            <th>{t('VALIDITY')}</th>
            <th>
              <div className="flex whitespace-nowrap">
                {t('STATUS')}
                <Tooltip
                  id="rate-table-status"
                  body={renderInfoIcon}
                  position="top"
                >
                  <div style={{ fontSize: '10px' }}>
                    {t('USER_RATE_CONFIRMED')}
                    <br />
                    {t('USER_CONFIRMATION_WAIT')}
                    <br />
                    {t('RATE_VALIDITY_EXPIRED')}
                    <br />
                    {t('USER_DECLINED_RATE_CARD')}
                  </div>
                </Tooltip>
              </div>
            </th>
            <th>{t('UPDATED')}</th>
            <th>{}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => {
            return (
              <tr key={i} className={i === items.length - 1 ? 'border-b' : ''}>
                <td>
                  <Checkbox
                    id={item.id}
                    checked={isRowChecked(item)}
                    onChange={handleToggleCheck(item, i)}
                  />
                </td>
                <td>
                  {item.customerType === 'carrier' ? (
                    <Badge color="blue" size="large" rounded>
                      {titleize(t('CARRIER', { count: 1 }))}
                    </Badge>
                  ) : (
                    <Badge color="green" size="large" rounded>
                      {titleize(t('SHIPPER', { count: 1 }))}
                    </Badge>
                  )}
                </td>
                <td>
                  <Link url={`/users/${item.customer}`}>{item.name}</Link>
                </td>
                <td>
                  {(item.originZoneName && item.originZoneName.trim()) ||
                    item.originCity}
                </td>
                <td>{item.originZip}</td>
                <td>
                  {(item.destinationZoneName &&
                    item.destinationZoneName.trim()) ||
                    item.destinationCity}
                </td>
                <td>{item.destinationZip}</td>
                <td>{formatMoney(item.rate, DEFAULT_CURRENCY)}</td>

                {!isNewFscFlow && (
                  <>
                    <td>{item.fsc ? item.fsc : '-'}</td>
                    <td>
                      {item.fscFlatRate ? t(sluggify(item.fscFlatRate)) : '-'}
                    </td>
                  </>
                )}

                <td>{item.reversible ? t('YES') : t('NO')}</td>
                <td>{item.commitment ? item.commitment : '-'}</td>
                <td
                  className="text-black whitespace-nowrap"
                  style={{ fontSize: '10px' }}
                >
                  {t('FROM_WITH_DATE', {
                    fromDate: moment(item.effectiveStart || null).format('L'),
                  })}
                  <br />
                  {t('TO_WITH_DATE', {
                    toDate: moment(item.effectiveEnd || null).format('L'),
                  })}
                </td>
                <td>{StatusBadge(item.status, t)}</td>
                <td className="whitespace-nowrap">
                  {formatTimeAgo(item.updatedAt)}
                </td>
                <td>
                  <div className="flex space-x-2 items-center">
                    {renderDocumentButton(item, i)}
                    {canUpdate &&
                      renderEditButton(item, i, handleEditButtonClick)}
                    {renderAuditTrailButton(item, i, setRateAudit)}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination
        className="border-none"
        perPage={perPage}
        page={page}
        pages={pages}
        total={total}
        showCount
        paginationContainerClassName="mx-auto my-0 sticky right-2/4"
        onPageClick={handlePageClick}
      />
    </div>
  );
}
